import {
  MODERN_REGIONS,
  REC_HEIGHT,
  REC_WIDTH,
  ctcDecode,
  prepareLine,
  recogniserTensor,
  regionsFor,
  clahe,
  LINE_HEIGHT,
  resizeCubic,
  type PreparedLine,
} from "@webscan/ocrprep.js";
import { grayPlane } from "@webscan/signature.js";
import { toU8 } from "@webscan/image.js";
import type { CollectorStrips, Strip } from "./collector-sampling";
import type * as OrtNamespace from "onnxruntime-web";
import { disposeTensors, withOrtLock } from "./ort-lock";

/**
 * The OCR half of the precision stage — `precision.js`'s reader, rehomed.
 *
 * OCR is what separates two printings of the same artwork *within one set*: the set
 * symbol confirms the set, but `mul 21` and `mul 151` share artwork, frame and symbol,
 * and only the printed collector number tells them apart. The pure pieces — line
 * preparation, the CTC decode, the parsers — live in `webscan/js` and are shared with
 * the classic client; this file owns only the session.
 *
 * The recogniser runs on WASM on purpose: it is a small CTC net, and handing it to
 * WebGPU would evict the embedder's weights between every card. Its runs still go
 * through the ort lock to avoid competing with identification. CPU OCR owns the
 * ordinary WASM runtime and its tensor constructors, independent of the GPU runtime.
 */

export interface OcrLine {
  text: string;
  confidence: number;
}

export interface CardText {
  title: OcrLine;
  collector: OcrLine;
  setInfo: OcrLine;
}

const EMPTY: OcrLine = { text: "", confidence: 0 };

/** Same inversion, cubic upscale and CLAHE as prepareLine, applied to sensor pixels. */
export function prepareStrip(strip: Strip): PreparedLine | null {
  const { data, width: w, height: h } = strip;
  if (w <= 0 || h <= 0 || data.length !== w * h * 4) return null;
  const gray = grayPlane(data, w, h, 0, 0, w, h);
  const mean = gray.reduce((sum, v) => sum + v, 0) / gray.length;
  if (mean < 110) for (let i = 0; i < gray.length; i++) gray[i] = 255 - gray[i];
  const scale = Math.max(1, LINE_HEIGHT / h);
  const width = Math.round(w * scale),
    height = Math.round(h * scale);
  const pixels = scale > 1 ? resizeCubic(gray, w, h, width, height) : toU8(gray);
  return { data: clahe(pixels, width, height), width, height };
}

export class OcrReader {
  /** Whether the graph accepts a batch axis; flips off permanently on first refusal. */
  private batches = true;

  private constructor(
    private readonly ort: typeof OrtNamespace,
    private readonly session: OrtNamespace.InferenceSession,
    private readonly charset: string[],
  ) {}

  static async create(
    ort: typeof OrtNamespace,
    model: Uint8Array,
    charsetText: string,
  ): Promise<OcrReader> {
    // rapidocr's class list: blank, then the table, then a space. Getting this wrong
    // produces fluent nonsense rather than an error, so it is built the one way.
    const charset = ["blank", ...charsetText.split("\n").map((l) => l.replace(/\r$/, "")), " "];
    const session = await ort.InferenceSession.create(model, {
      executionProviders: ["wasm"],
      graphOptimizationLevel: "all",
    });
    return new OcrReader(ort, session, charset);
  }

  /**
   * Read the title bar and the bottom-left info block off a normalised card.
   *
   * The title box is the same across frame eras; the collector block only exists on
   * 2015-and-later frames, so it is cropped with the modern preset regardless of what
   * the leading candidate claims to be — reading a copyright line and finding no
   * number in it is the correct outcome for an older card.
   */
  async read(
    card: Uint8ClampedArray,
    frame: string,
    fullArt: boolean,
    strips?: CollectorStrips | null,
  ): Promise<CardText> {
    const regions = regionsFor(frame, fullArt);
    const boxes = [regions.title, MODERN_REGIONS.collector, MODERN_REGIONS.set_info];
    const lines = boxes.map((box, i) =>
      strips && i > 0
        ? prepareStrip(i === 1 ? strips.collector : strips.setInfo)
        : prepareLine(card, box),
    );
    return this.readLines(this.ort, lines);
  }

  /** A latched reprint needs only two CTC lines; embedding/ORB/title are not repeated. */
  readCollector(strips: CollectorStrips): Promise<CardText> {
    return this.readLines(this.ort, [
      null,
      prepareStrip(strips.collector),
      prepareStrip(strips.setInfo),
    ]);
  }

  private async readLines(
    ort: typeof OrtNamespace,
    lines: Array<PreparedLine | null>,
  ): Promise<CardText> {
    const usable = lines
      .map((line, i) => ({ line, i }))
      .filter((entry): entry is { line: NonNullable<(typeof lines)[0]>; i: number } =>
        Boolean(entry.line),
      );
    const out: OcrLine[] = [EMPTY, EMPTY, EMPTY];
    if (!usable.length) return { title: out[0], collector: out[1], setInfo: out[2] };

    const stride = 3 * REC_HEIGHT * REC_WIDTH;
    const decode = (logits: OrtNamespace.Tensor, slot: number): OcrLine => {
      const [, steps, classes] = logits.dims as number[];
      const data = logits.data as Float32Array;
      return ctcDecode(
        data.subarray(slot * steps * classes, (slot + 1) * steps * classes),
        steps,
        classes,
        this.charset,
      );
    };

    // One forward for all three crops where the graph allows it — on WASM three
    // separate runs pay the interpreter's per-run overhead three times.
    if (this.batches) {
      try {
        const batch = new Float32Array(usable.length * stride);
        usable.forEach((entry, slot) => batch.set(recogniserTensor(entry.line), slot * stride));
        const tensor = new ort.Tensor("float32", batch, [usable.length, 3, REC_HEIGHT, REC_WIDTH]);
        let result: OrtNamespace.InferenceSession.ReturnType | null = null;
        try {
          result = await withOrtLock(() =>
            this.session.run({ [this.session.inputNames[0]]: tensor }),
          );
          const logits = result[this.session.outputNames[0]];
          usable.forEach((entry, slot) => {
            out[entry.i] = decode(logits, slot);
          });
          return { title: out[0], collector: out[1], setInfo: out[2] };
        } finally {
          disposeTensors(tensor, ...Object.values(result ?? {}));
        }
      } catch {
        // A graph with its batch axis pinned refuses the bind. Remember, and never try
        // again this session — the fallback below is correct, just slower.
        this.batches = false;
      }
    }

    for (const entry of usable) {
      const tensor = new ort.Tensor("float32", recogniserTensor(entry.line), [
        1,
        3,
        REC_HEIGHT,
        REC_WIDTH,
      ]);
      let result: OrtNamespace.InferenceSession.ReturnType | null = null;
      try {
        result = await withOrtLock(() =>
          this.session.run({ [this.session.inputNames[0]]: tensor }),
        );
        out[entry.i] = decode(result[this.session.outputNames[0]], 0);
      } finally {
        disposeTensors(tensor, ...Object.values(result ?? {}));
      }
    }
    return { title: out[0], collector: out[1], setInfo: out[2] };
  }
}
